import { User } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import * as React from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { Conversation, ConversationParticipant } from '@/types/chat.type'

interface ViewParticipantsDialogProps {
  conversation: Conversation
  onClose: () => void
}

export const ViewParticipantsDialog: React.FC<ViewParticipantsDialogProps> = ({ conversation, onClose }) => {
  const router = useRouter()

  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [onClose])

  // Add null checks to prevent TypeError
  if (!conversation || !conversation.participants) {
    return null
  }

  const handleUserClick = (participant: ConversationParticipant) => {
    // Close the dialog first
    onClose()

    // Use profileId if available, otherwise fall back to user ID
    // Note: profileId might not be available in the participant user object
    const userWithProfileId = participant.user as typeof participant.user & { profileId?: string }
    const profileId = userWithProfileId.profileId || participant.user.id.toString()
    router.push(`/user/${profileId}`)
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Participants ({conversation.participants.length})</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {conversation.participants.map(participant => (
            <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                  {participant.user.avatar ? (
                    <Image
                      src={participant.user.avatar}
                      alt={`${participant.user.firstName} ${participant.user.lastName}`}
                      width={40}
                      height={40}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  )}
                </div>
                <div>
                  <button
                    onClick={() => handleUserClick(participant)}
                    className="font-medium text-left hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                  >
                    {participant.user.firstName} {participant.user.lastName}
                  </button>
                  <div className="text-sm text-gray-500 capitalize">{participant.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
